import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder, faFile, faTrashArrowUp, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { listTrash, restoreNode, purgeNode, emptyTrash } from "./filesApi";

/**
 * RecycleBinApp — the Recycle Bin window. Lists the user's deleted items
 * (across all drives), with Restore and Delete-permanently per item, and an
 * "Empty Recycle Bin" action. Deletes in the File Explorer move items here
 * (soft delete) instead of erasing them.
 */
const RecycleBinApp = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listTrash());
    } catch (e) {
      setError(e?.message || "Failed to load the Recycle Bin");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const restore = (node) => run(() => restoreNode(node.id));
  const purge = (node) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Permanently delete "${node.name}"? This can't be undone.`)) return;
    run(() => purgeNode(node.id));
  };
  const empty = () => {
    if (!items.length) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Permanently delete all items in the Recycle Bin?")) return;
    run(() => emptyTrash());
  };

  return (
    <div className="webtop-files">
      <div className="webtop-files__toolbar">
        <div className="webtop-files__crumbs">
          <span className="webtop-files__crumbbtn">Recycle Bin</span>
        </div>
        <div className="webtop-files__actions">
          <button type="button" className="webtop-files__actbtn" onClick={empty} disabled={busy || !items.length} title="Empty Recycle Bin">
            <FontAwesomeIcon icon={faTrashCan} /> Empty Recycle Bin
          </button>
        </div>
      </div>

      {error && <div className="webtop-files__error" role="alert">{error}</div>}

      <div className="webtop-files__body">
        {loading ? (
          <div className="webtop-files__empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="webtop-files__empty">The Recycle Bin is empty.</div>
        ) : (
          <div className="webtop-bin__list">
            {items.map((node) => (
              <div key={node.id} className="webtop-bin__row">
                <FontAwesomeIcon
                  icon={node.type === "folder" ? faFolder : faFile}
                  className={`webtop-bin__icon webtop-bin__icon--${node.type}`}
                />
                <span className="webtop-bin__name" title={node.name}>{node.name}</span>
                <span className="webtop-bin__scope">{node.scope}</span>
                <div className="webtop-bin__rowactions">
                  <button type="button" className="webtop-bin__btn" onClick={() => restore(node)} disabled={busy} title="Restore">
                    <FontAwesomeIcon icon={faTrashArrowUp} /> Restore
                  </button>
                  <button type="button" className="webtop-bin__btn webtop-bin__btn--danger" onClick={() => purge(node)} disabled={busy} title="Delete permanently">
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecycleBinApp;
